import {COMMON_HEADER, MANDATORY_FIELD} from "../NewResearcherConstants";
import RegistrationModel from "./model/RegistrationModel";
import {FormProvider, useForm} from "react-hook-form";
import AppContext from "../../../framework/AppContext";
import {useNavigate} from "react-router-dom";
import React, {useEffect, useRef, useState} from "react";
import apiClient from "../../../framework/web/ApiClient";
import axios, {AxiosError} from "axios";
import AppBar from "../../../framework/component/frame/AppBar";
import PageHeader from "../../../framework/component/frame/PageHeader";
import MessagePanel from "../../../framework/component/container/MessagePanel";
import Form from "react-bootstrap/Form";
import {MessagePanelType} from "../../../framework/component/container/MessagePanelType";
import FormPanel from "../../../framework/component/form/FormPanel";

import "./Registration.css"
import FormNavigationPanel from "../../../framework/component/form/navigation/FormNavigationPanel";
import PersonalInformation from "./formPanels/PersonalInformation";
import ContactInformation from "./formPanels/ContactInformation";
import ProfessionalDetails from "./formPanels/ProfessionalDetails";
import Institutes from "./formPanels/Institutes";
import ResearcherInterests from "./formPanels/ResearcherInterests";
import Others from "./formPanels/Others";
import {Toast, ToastContainer} from "react-bootstrap";
import LoadingScreen from "../../../framework/utility-pages/LoadingScreen";

/**
 * The extra information we will be showing in the header
 */
const TITLE_SECTION_CONTENT = <>
  {COMMON_HEADER}
  {MANDATORY_FIELD}
</>;

/**
 * The URL the cancel btn will redirect to
 */
const REDIRECT_URL = "/ams";

/**
 * URL for submit request
 */
const UI_API_PATH = "/ams/ui-api/v1/researchers/registration";

/**
 * Needed for errors containing field ID
 */
type RegistrationModelField = keyof RegistrationModel | "root";

/**
 * This is the main component that defines the Registration page.
 */
export default function Registration() {
  const [hasConsentedToTAndC, setHasConsentedToTAndC] = useState<boolean>(false);
  const [isCodeRepoMandatory, setIsCodeRepoMandatory] = useState<boolean>(false);
  const [successToastMessage, setSuccessToastMessage] = useState<string | null>(null);
  const [errorSavingShow, setErrorSaving] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);

  // These are to be used to scroll to top in case of error so user can see the error panel
  const globalErrorRef = useRef<HTMLDivElement>(null);
  const scrollToError = () => {
    formRef.current?.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const formRef = useRef<HTMLDivElement>(null);
  const personalInformationRef = useRef<HTMLDivElement>(null);
  const contactInformationRef = useRef<HTMLDivElement>(null);
  const professionalDetailsRef = useRef<HTMLDivElement>(null);
  const institutesRef = useRef<HTMLDivElement>(null);
  const researcherInterestsRef = useRef<HTMLDivElement>(null);
  const othersRef = useRef<HTMLDivElement>(null);

  const methods = useForm<RegistrationModel>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {},
    shouldFocusError: true
  });
  const {
    formState: {isSubmitting, isSubmitSuccessful}
  } = methods;

  let context!: React.ContextType<typeof AppContext>;

  const navigate = useNavigate();

  /**
   * Make initial API call to load the data in redis (in case this was open via invitation link) and
   * necessary system preferences (TODO Implement this in future ticket)
   */
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await apiClient.get(UI_API_PATH, {});

        if (response.status === 200 && response.data.data) {
          const resData = response.data.data;
          methods.setValue("personalInfo.firstName", resData.firstName);
          methods.setValue("personalInfo.lastName", resData.lastName);
          methods.setValue("contactInfo.contactEmail", resData.email)

          if (resData.primaryInstituteId && resData.primaryInstituteName) {
            methods.setValue("institutes.primaryInstitute.instituteId", resData.primaryInstituteId);
            methods.setValue("institutes.primaryInstitute.instituteName", resData.primaryInstituteName);
            methods.setValue("institutes.primaryInstitute.email", resData.email);
          }

          setIsCodeRepoMandatory(resData.codeRepoCollectionMandatory);
        }

        setLoading(false);
      } catch (error: AxiosError | any) {
        // If we get a 400 it falls here so we need to determine if we need to redirect somewhere
        if (!axios.isAxiosError(error)) {
          console.warn(`There was an error with the initial API call (error = ${error})`);
          return;
        }
      }
    }
    fetchFormData();
  }, [methods.setValue]);

  const toBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = (error) => reject(error)
      })

  /**
   * Method to handle the submission of the form
   */
  const onSubmit = async (data: RegistrationModel, e: any) => {
    // Prevent double submission
    if (isSubmitting || isSubmitSuccessful) {
      return;
    }

    // Clear previous errors
    methods.clearErrors("root");

    // Finish preparing the data for the request
    // Complaint file
    if (data.professionalDetail.complaintFile.length > 0) {
      await toBase64(data.professionalDetail.complaintFile[0])
      .then((res) => {
        data.professionalDetail.complaint = {
          name: data.professionalDetail.complaintFile[0].name,
          content: res
        };
      });
    }

    // CV
    if (data.professionalDetail.cvfile.length > 0) {
      await toBase64(data.professionalDetail.cvfile[0])
      .then((res) => {
        data.professionalDetail.cv = {name: data.professionalDetail.cvfile[0].name, content: res};
      });
    }

    // MRC Certificate
    if (data.other.mrcfiles.length > 0) {
      await toBase64(data.other.mrcfiles[0])
      .then((res) => {
        data.other.mrc = {name: data.other.mrcfiles[0].name, content: res};
      });
    }
    data.interests = data.allinterests.flat()

    try {
      const response = await apiClient.post(UI_API_PATH, data);
      if (response.status === 201) {
        console.log('Registration form submitted');
        setSuccessToastMessage(`Form submitted at ${
            new Date().toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
        }`);
      } else {
        response.data.errors?.forEach(({code, message, fieldId}: {
          code: string,
          message: string,
          fieldId: RegistrationModelField
        }) => {
          // If field ID empty or null then it's a root error
          const errorFieldId = (!fieldId) ? "root" : fieldId;
          methods.setError(errorFieldId, {type: "server", message: message});
        })
        console.warn(`Error when submitting registration form. (status: ${response.status}, body: ${response.statusText})`);
        if (!methods.formState.errors.root) {
          methods.setError("root", {
            type: "manual",
            message: "Error when submitting the form. Please verify the data you have entered"
          });
        }
        setErrorSaving(true);
        scrollToError(); // TODO This at the moment will scroll to top, need to change to scroll to error
      }
    } catch (error: AxiosError | any) {
      // Non expected error
      if (axios.isAxiosError(error) && error.status !== 400) {
        console.warn(`Error when submitting registration form. (error = ${error})`);
        methods.setError("root", {
          type: "server",
          message: "Unexpected error when registering. Please try again later."
        });
        return;
      }

      // API call error
      error.response?.data.errors?.forEach(({code, message, field}: {
        code: string,
        message: string,
        field: RegistrationModelField
      }) => {
        // If field ID empty or null then it's a root error
        const errorFieldId = (!field) ? "root" : field;
        methods.setError(errorFieldId, {type: "server", message: message});
      })
      console.warn(`Error when submitting registration form. (status: ${error.response?.status}, body: ${error.response?.statusText})`);
      if (!methods.formState.errors.root) {
        methods.setError("root", {
          type: "manual",
          message: "Error when submitting the form. Please verify the data you have entered"
        });
      }
      setErrorSaving(true);
      scrollToError(); // TODO This at the moment will scroll to top, need to change to scroll to error
    }
  }

  if (loading) {
    return <AppContext.Provider value={context}>
      <AppBar/>
      <LoadingScreen></LoadingScreen>
    </AppContext.Provider>
  }

  return <div id={"registrationContentPage"} className="page">
    <AppContext.Provider value={context}>
      <AppBar/>
      <PageHeader title={"Registration"}>{TITLE_SECTION_CONTENT}</PageHeader>
      <div className={"content-area no-scroll"}>
        <FormProvider {...methods}>
          <Form id={"registrationForm"} className={"registration-form"}
                onSubmit={methods.handleSubmit(onSubmit)} noValidate
                data-testid="registration-form">
            <div className={"side-menu"}>
              {/* Submit & Cancel buttons*/}
              <FormNavigationPanel parentFormContainer={formRef} listOfPanels={[
                {label: "Personal Information", panelReference: personalInformationRef},
                {label: "Contact Information", panelReference: contactInformationRef},
                {label: "Professional Details", panelReference: professionalDetailsRef},
                {label: "Institutes", panelReference: institutesRef},
                {label: "Researcher Interests", panelReference: researcherInterestsRef},
                {label: "Others", panelReference: othersRef}
              ]}/>
              <div id={"registrationSubmitArea"} className={"confirm-form"}>
                <div id={`registrationConfirmation`} className="form-check">
                  <input id={`registrationConfirmationCheck`} className="form-check-input"
                         type="checkbox"
                         value=""
                         onChange={(event) => {
                           setHasConsentedToTAndC(event.target.checked)
                         }}/>
                  <label id={`registrationConfirmationLabel`} className="form-check-label"
                         htmlFor={`registrationConfirmationCheck`}>
                    I agree to comply with all Terms and Conditions
                  </label>
                </div>
                <div id={"registrationBtnArea"} className={"btn-area"}>
                  <button id="registrationSubmitBtn" type="submit" className="btn btn-primary"
                          data-testid="submit" disabled={!hasConsentedToTAndC || isSubmitting || isSubmitSuccessful}>Submit
                  </button>
                  <a id="registrationCancelBtn" href={REDIRECT_URL}
                     className="btn btn-danger">Cancel <i
                      className="bi bi-trash3"/></a>
                </div>
              </div>
            </div>
            {/* Form */}
            <div className={"form-area"} ref={formRef}>
              {/*If any unexpected error occurs*/}
              {methods.formState.errors.root && (
                  <div ref={globalErrorRef}>
                    <MessagePanel title={"Unexpected error"} panelType={MessagePanelType.ERROR}
                                  data-testid="root-error">
                      {methods.formState.errors.root.message}
                    </MessagePanel>
                  </div>
              )}
              <FormPanel title={"Personal Information"} panelReference={personalInformationRef}>
                <PersonalInformation/>
              </FormPanel>
              <FormPanel title={"Contact Information"} panelReference={contactInformationRef}>
                <ContactInformation/>
              </FormPanel>
              <FormPanel title={"Professional Details"} panelReference={professionalDetailsRef}>
                <ProfessionalDetails codeRepoMandatory={isCodeRepoMandatory}/>
              </FormPanel>
              <FormPanel title={"Institutes"} panelReference={institutesRef}>
                <Institutes/>
              </FormPanel>
              <FormPanel title={"Researcher Interests"} panelReference={researcherInterestsRef}>
                <ResearcherInterests/>
              </FormPanel>
              <FormPanel title={"Others"} panelReference={othersRef}>
                <Others/>
              </FormPanel>
            </div>
          </Form>
        </FormProvider>
        {/*Toast for on submit*/}
        <ToastContainer id="regFormToastContainer" position="bottom-end"
                        className="p-3 toast-container">
          {/*Saved Draft toast*/}
          <Toast id="regFormSavedToastToast" onClose={() => {
            navigate('/');
          }} show={successToastMessage !== null}
                 delay={3000} autohide>
            <Toast.Header id="regFormSavedToastToastHeader">
              <strong id="regFormSavedToastToastHeaderText" className="me-auto"><i
                  id="regFormSavedToastToastHeaderIcon"
                  className="bi bi-check-circle-fill text-success"/>Registration submitted</strong>
            </Toast.Header>
            <Toast.Body id="regFormSavedToastToastBody">{successToastMessage}</Toast.Body>
          </Toast>
          {/*Error toast*/}
          <Toast id="regFormErrorToastToast" onClose={() => setErrorSaving(false)}
                 show={errorSavingShow}
                 delay={5000} autohide>
            <Toast.Header id="regFormErrorToastToastHeader">
              <strong id="regFormErrorToastToastHeaderText" className="me-auto"><i
                  id="regFormErrorToastToastHeaderIcon"
                  className="bi bi-exclamation-circle-fill text-danger"/> Error</strong>
            </Toast.Header>
            <Toast.Body id="regFormErrorToastToastBody">Error submitting the registration. Please
              ensure all the
              data introduced is correct.</Toast.Body>
          </Toast>
        </ToastContainer>
      </div>
    </AppContext.Provider>
  </div>
      ;
}